import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { Form, FormItem, useForm } from "@portal/BaseComponents/Form/AntForm";
import { InputNumber } from "@portal/BaseComponents/Input/AntInput";
import { OnInputNumberChange } from "@portal/BaseComponents/Input/type";
import { useState } from "react";
import { FilterFooter } from "../../Footer/FilterFooter";
import {
  minMaxKeys,
  useMinmaxValidation,
} from "../../hooks/useMinmaxValidaton";
import { CustomRange, CustomRangeValue } from "../../type";

const t = {
  min: "Min",
  max: "Max",
};

const wrapperStyles: React.CSSProperties = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-8)",
};

interface MinMaxElementProps {
  initialValue?: CustomRangeValue<typeof minMaxKeys>;
  onSubmit: (value: CustomRangeValue<typeof minMaxKeys>) => void;
  onCancel: () => void;
}
function MinMaxElement(props: MinMaxElementProps) {
  const { initialValue, onCancel, onSubmit } = props;

  const [form] = useForm();
  const [minMax, setMinMax] =
    useState<CustomRange<typeof minMaxKeys>>(initializeValue);

  function initializeValue(): CustomRange<typeof minMaxKeys> {
    return {
      min: {
        value: initialValue?.min ? initialValue.min : 0,
        validateStatus: initialValue?.min
          ? isNaN(initialValue.min)
            ? "error"
            : undefined
          : undefined,
      },
      max: {
        value: initialValue?.max ? initialValue.max : 0,
        validateStatus: initialValue?.max
          ? isNaN(initialValue.max)
            ? "error"
            : undefined
          : undefined,
      },
    };
  }
  const { minValidation, maxValidation } = useMinmaxValidation();

  const isDisabled =
    minMax.min.validateStatus === "error" ||
    minMax.max.validateStatus === "error";
  function handleMinChange(...args: OnInputNumberChange) {
    const [value] = args;
    const validationStatus = minValidation(Number(value), minMax);

    setMinMax((perV) => {
      return {
        min: { ...validationStatus, value: Number(value) },
        max:
          validationStatus.validateStatus === "success"
            ? { ...perV.max, errorMsg: null, validateStatus: "success" }
            : { ...perV.max },
      };
    });
  }
  function handleMaxChange(...args: OnInputNumberChange) {
    const [value] = args;

    const validationStatus = maxValidation(Number(value), minMax);

    setMinMax((perV) => {
      return {
        min:
          validationStatus.validateStatus === "success"
            ? { ...perV.min, errorMsg: null, validateStatus: "success" }
            : { ...perV.min },
        max: { ...validationStatus, value: Number(value) },
      };
    });
  }

  function handleSubmit() {
    onSubmit({ min: minMax.min.value, max: minMax.max.value });
  }
  const Min = (
    <FormItem
      name="min-item"
      label={t.min}
      initialValue={minMax.min.value}
      validateStatus={minMax.min.validateStatus}
      help={minMax.min.errorMsg}
    >
      <InputNumber
        controls={false}
        style={{ width: "100%" }}
        onChange={handleMinChange}
        min={0}
      />
    </FormItem>
  );

  const Max = (
    <FormItem
      name="max-item"
      label={t.max}
      initialValue={minMax.max.value}
      validateStatus={minMax.max.validateStatus}
      help={minMax.max.errorMsg}
    >
      <InputNumber
        controls={false}
        style={{ width: "100%" }}
        onChange={handleMaxChange}
        min={0}
      />
    </FormItem>
  );

  const MixMaxForm = (
    <Form
      form={form}
      name="min-max-form"
      layout="vertical"
      style={wrapperStyles}
      requiredMark={false}
    >
      {Min}
      {Max}
    </Form>
  );

  const footer = (
    <FilterFooter
      onCancel={onCancel}
      onSubmit={handleSubmit}
      disable={isDisabled}
    />
  );

  return (
    <Flex vertical style={{ maxWidth: "240px" }}>
      {MixMaxForm}
      {footer}
    </Flex>
  );
}

export { MinMaxElement };
